import {
  ANALYTICS_GLOBAL_CLICK_EVENT,
  ANALYTICS_GLOBAL_PAGE_LOAD_EVENT,
  HONORS_CLICK_ID,
  HONORS_ERRORS,
  HONORS_PAGE_DETAIL_1,
  HONORS_PAGE_PRIMARY_CATEGORY,
  HONORS_PAGE_TYPE,
  HONORS_UNIVERSAL_SEGMENTATION,
  setDigitalData,
  trackEvent,
} from '@dx-ui/config-metrics';
import type { JoinFormMetrics } from '../types/join-form-analytics';

export type JoinValidationErrorPayload = {
  formFields: string;
  alertMessage: string;
};

export type JoinSuccessPayload = {
  profileId: string;
};

export type JoinPageStopErrorPayload = {
  errorCode: number;
  alertMessage: string;
};

export type JoinAnalytics = {
  onJoinInitiate?: () => void;
  onJoinSuccess?: (payload: JoinSuccessPayload) => void;
  onJoinValidationError?: (payload: JoinValidationErrorPayload) => void;
  onJoinPageStopError?: (payload: JoinPageStopErrorPayload) => void;
  isLegacyJoinAnalyticsEnabled?: boolean;
};

export function setJoinFormErrors(
  formErrors: { errors: string[] | string; eventIndex?: number }[]
) {
  const { errors = '', eventIndex = 0 } = formErrors[0] ?? {};
  const fields = Array.isArray(errors) ? errors.join(', ') : errors;

  setDigitalData(`event[${eventIndex}].eventInfo.eventAction`, 'error');
  setDigitalData('error.errorType', HONORS_ERRORS.ERROR_TYPE);
  setDigitalData('error.formFields', fields);
  setDigitalData('page.pageInfo.formError', fields);
}

export function setJoinFormErrorMessages(messages: string | string[]) {
  const message = Array.isArray(messages) ? messages.join(',') : messages;

  setDigitalData('click.clickID', HONORS_CLICK_ID.FORM_VALIDATION);
  setDigitalData('error.alertMessage', message);
  setDigitalData('page.pageInfo.formErrorMessage', message);
}

export function trackJoinForm() {
  setDigitalData('click.clickID', HONORS_CLICK_ID.JOIN_INITIATE_STANDARD);
  setDigitalData('universal.segmentation', HONORS_UNIVERSAL_SEGMENTATION.STANDARD_JOIN);
  trackEvent(ANALYTICS_GLOBAL_CLICK_EVENT);
}

export function trackJoinModalView() {
  setDigitalData('global.page.primaryCategory', HONORS_PAGE_PRIMARY_CATEGORY.HONORS);
  setDigitalData('global.page.pageType', HONORS_PAGE_TYPE.JOIN);
  setDigitalData('global.page.pageDetail1', HONORS_PAGE_DETAIL_1.JOIN_HILTON_HONORS_FORM);
  trackEvent(ANALYTICS_GLOBAL_PAGE_LOAD_EVENT, {}, true);
}

export function trackJoinModalSuccess(hHonorsNumber: string) {
  setDigitalData('global.page.primaryCategory', HONORS_PAGE_PRIMARY_CATEGORY.HONORS);
  setDigitalData('global.page.pageType', HONORS_PAGE_TYPE.JOIN);
  setDigitalData('global.page.pageDetail1', HONORS_PAGE_DETAIL_1.CONFIRMATION);
  if (hHonorsNumber) {
    setDigitalData('global.user.profileID', hHonorsNumber);
  }
  setDigitalData('universal.segmentation', HONORS_UNIVERSAL_SEGMENTATION.STANDARD_JOIN);
  trackEvent(ANALYTICS_GLOBAL_PAGE_LOAD_EVENT, {}, true);
}

export function trackJoinFormPageStopErrors(
  props: {
    errorCode: number;
    message: string;
  }[]
) {
  const { errorCode = 0, message = '' } = props[0] ?? {};

  setDigitalData('click.clickID', HONORS_CLICK_ID.PAGE_STOP);
  setDigitalData('error.errorType', HONORS_ERRORS.ERROR_TYPE);
  setDigitalData('error.errorCode', errorCode);
  setDigitalData('error.alertMessage', message);
  trackEvent(ANALYTICS_GLOBAL_CLICK_EVENT, {}, true);
}

export const windowJoinFormMetrics: JoinFormMetrics = {
  setPageInfo: (key, value) =>
    setDigitalData(`global.page.${key}`, Array.isArray(value) ? value.join(',') : value),
  setProperty: (key, value) => setDigitalData(key, Array.isArray(value) ? value.join(',') : value),
  trackEvent: (event) => trackEvent(event, {}, true),
  setFormErrors: (formErrors) => setJoinFormErrors(formErrors),
  setFormErrorMessages: (messages) => setJoinFormErrorMessages(messages),
  trackJoinForm,
  trackJoinFormPageStopErrors: (props) => trackJoinFormPageStopErrors(props),
  trackJoinModalView,
};

export const sendErrorAnalytics = (
  metrics: JoinFormMetrics,
  formErrors: string[],
  formErrorsMessages: string[]
) => {
  metrics.setPageInfo('alertBoxText', formErrors);
  metrics.setFormErrors([{ errors: formErrors.join('|') }]);
  metrics.setFormErrorMessages(formErrorsMessages.join('|'));
  metrics.setProperty('page.attributes.actionDetail', 'formError');
  metrics.trackEvent('alertBoxText');
  metrics.trackEvent(ANALYTICS_GLOBAL_CLICK_EVENT);
};

type DispatchJoinInitiateParams = {
  trackLegacy?: () => void;
  analytics?: JoinAnalytics;
};

export function dispatchJoinInitiate({ trackLegacy, analytics }: DispatchJoinInitiateParams = {}) {
  const isLegacyJoinAnalyticsEnabled = analytics?.isLegacyJoinAnalyticsEnabled ?? true;

  if (isLegacyJoinAnalyticsEnabled) {
    trackLegacy?.();
    return;
  }

  analytics?.onJoinInitiate?.();
}

type DispatchJoinValidationErrorParams = {
  trackLegacy?: () => void;
  analytics?: JoinAnalytics;
  payload: JoinValidationErrorPayload;
};

export function dispatchJoinValidationError({
  trackLegacy,
  analytics,
  payload,
}: DispatchJoinValidationErrorParams) {
  const isLegacyJoinAnalyticsEnabled = analytics?.isLegacyJoinAnalyticsEnabled ?? true;

  if (isLegacyJoinAnalyticsEnabled) {
    trackLegacy?.();
    return;
  }

  analytics?.onJoinValidationError?.(payload);
}

type DispatchJoinPageStopErrorParams = {
  trackLegacy?: () => void;
  analytics?: JoinAnalytics;
  payload: JoinPageStopErrorPayload;
};

export function dispatchJoinPageStopError({
  trackLegacy,
  analytics,
  payload,
}: DispatchJoinPageStopErrorParams) {
  const isLegacyJoinAnalyticsEnabled = analytics?.isLegacyJoinAnalyticsEnabled ?? true;

  if (isLegacyJoinAnalyticsEnabled) {
    trackLegacy?.();
    return;
  }

  analytics?.onJoinPageStopError?.(payload);
}

type DispatchJoinSuccessParams = {
  trackLegacy?: () => void;
  analytics?: JoinAnalytics;
  payload: JoinSuccessPayload;
};

export function dispatchJoinSuccess({
  trackLegacy,
  analytics,
  payload,
}: DispatchJoinSuccessParams) {
  const isLegacyJoinAnalyticsEnabled = analytics?.isLegacyJoinAnalyticsEnabled ?? true;

  if (isLegacyJoinAnalyticsEnabled) {
    trackLegacy?.();
    return;
  }

  analytics?.onJoinSuccess?.(payload);
}
